import React, { useEffect, useState } from "react";
import "./leaveRequest.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import FlexBetween from "shared/FlexBetween";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationDialog from "shared/ConfirmationDialog";

function LeaveRequest() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState("");
  const [user, setUser] = useState({});
  const [name, setName] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [sickLeaves, setSickLeaves] = useState(null);
  const [privilegeLeaves, setPrivilegeLeaves] = useState(null);
  const [totalDays, setTotalDays] = useState(0);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [minEndDate, setMinEndDate] = useState();
  const [loggedInUser, setLoggedInUser] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL + "profilepage", {
        withCredentials: true,
      })
      .then((response) => {
        setUser(response.data);
        setEmployeeId(response.data.employeeId);
        setLoggedInUser(response.data.name);
        
        // Only fetch leave limits if we have an employee ID
        if (response.data.employeeId) {
          axios
            .get(
              `${process.env.REACT_APP_API_URL}leaves/getLeaveLimits/${response.data.employeeId}`
            )
            .then((response) => {
              setSickLeaves(response.data.sickLeaves);
              setPrivilegeLeaves(response.data.privilegeLeaves);
            })
            .catch((error) => {
              console.error("Error fetching leave details:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []); // Removed employeeId from dependency array to prevent unnecessary re-renders

  // Validation function for description whitespace
  const validateDescription = (text) => {
    return text.trim() !== "";
  };

  // Validate date range
  const validateDateRange = (start, end) => {
    if (!start || !end) return true; // Will be caught by required field validation
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    return startDate <= endDate;
  };

  // Current date for date validation
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate leave type
    if (!leaveType) {
      newErrors.leaveType = "Leave Type is required";
      isValid = false;
    }

    // Validate start date
    if (!startDate) {
      newErrors.startDate = "Start Date is required";
      isValid = false;
    }

    // Validate end date
    if (!endDate) {
      newErrors.endDate = "End Date is required";
      isValid = false;
    } else if (!validateDateRange(startDate, endDate)) {
      newErrors.endDate = "End Date cannot be before Start Date";
      isValid = false;
    }

    // Validate description - check for empty or only whitespace
    if (!description) {
      newErrors.description = "Description is required";
      isValid = false;
    } else if (!validateDescription(description)) {
      newErrors.description = "Description cannot be empty or contain only whitespace";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setOpenConfirmation(false);
      return;
    }

    // Calculate total days
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    // Calculate the difference in days
    const differenceDays = Math.round((endDateObj - startDateObj) / oneDay);

    // Add 1 to include both start and end dates in the count
    const calculatedTotalDays = differenceDays + 1;
    setTotalDays(calculatedTotalDays);

    // Check if leave type is sick leave and total days exceed available sick leaves
    if (leaveType === "Sick Leave" && calculatedTotalDays > sickLeaves) {
      // Show toast notification for sick leave limit exceeded
      toast.error(
        "Sick Leave limit exceeded. Try another leave or reduce the number of leaves."
      );
      return;
    }

    // Check if leave type is privilege leave and total days exceed available privilege leaves
    if (
      leaveType === "Privilege Leave" &&
      calculatedTotalDays > privilegeLeaves
    ) {
      // Show toast notification for privilege leave limit exceeded
      toast.error(
        "Privilege Leave limit exceeded. Try another leave or reduce the number of leaves."
      );
      return;
    }
    axios
      .post(process.env.REACT_APP_API_URL + "leaves", {
        employeeId: employeeId,
        name: name, // Include the name value
        leaveType,
        startDate,
        endDate,
        totalDays: calculatedTotalDays,
        description: description.trim(), // Trim description before sending
        status: "Pending",
        updatedBy: loggedInUser
      })
      .then((response) => {
        console.log(response.data);
        // Redirect or handle success as needed
        setOpenConfirmation(false);
        navigate(-1); // Redirect to a success page, for example
      })
      .catch((error) => {
        setOpenConfirmation(false);
        console.error("Error creating time sheet:", error);
        if (error.response && error.response.status === 400) {
          toast.error(error.response.data.error || "Bad Request");
        } else {
          toast.error("An error occurred while creating the leave request.");
        }
      });
  };

  useEffect(() => { }, [totalDays, sickLeaves, privilegeLeaves, leaveType]);

  //Confirmation box
  const handleOpenConfirmation = () => {
    if (validateForm()) {
      setOpenConfirmation(true);
    }
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  useEffect(() => {
    setMinEndDate(startDate);
  }, [startDate]);

  return (
  <div className="page-container">
    <div className="form-wrapper">
      <div className="header-section">
        {/* Back button integrated into header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          marginBottom: '20px' 
        }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ 
              color: "#04497a",
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(4, 73, 122, 0.08)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <h2 className="page-title" style={{ margin: 0 }}>Create Leave Request</h2>
        </div>
        
        <div className="leave-info">
          <div className="employee-id">
            ID: <span>{employeeId}</span>
          </div>
          <div className="leave-balance">
            <div className="balance-item">
              <label>Privilege Leave:</label>
              <span>{privilegeLeaves}</span>
            </div>
            <div className="balance-item">
              <label>Sick Leave:</label>
              <span>{sickLeaves}</span>
            </div>
          </div>
        </div>
      </div>
  
        <form onSubmit={(e) => {
          e.preventDefault();
          handleOpenConfirmation();
        }}>
          <div className="form-content">
            <div className="form-row">
              <div className="input-group">
                <label>Leave Type<span className="required">*</span></label>
                <select
                  value={leaveType}
                  onChange={(e) => {
                    setLeaveType(e.target.value);
                    setErrors({...errors, leaveType: null});
                  }}
                  className={`form-input ${errors.leaveType ? 'input-error' : ''}`}
                >
                  <option value="">Select Leave Type</option>
                  <option value="Privilege Leave">Privilege Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                </select>
                {errors.leaveType && (
                  <span className="error-message">{errors.leaveType}</span>
                )}
              </div>
            </div>
  
            <div className="form-row date-section">
              <div className="input-group">
                <label>From<span className="required">*</span></label>
                <input
                  type="date"
                  value={startDate}
                  min={getCurrentDate()}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setErrors({...errors, startDate: null});
                    // Clear end date if it's now invalid
                    if (endDate && new Date(e.target.value) > new Date(endDate)) {
                      setEndDate('');
                    }
                  }}
                  className={`form-input ${errors.startDate ? 'input-error' : ''}`}
                />
                {errors.startDate && (
                  <span className="error-message">{errors.startDate}</span>
                )}
              </div>
              <div className="input-group">
                <label>To<span className="required">*</span></label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setErrors({...errors, endDate: null});
                  }}
                  disabled={!startDate}
                  min={minEndDate}
                  className={`form-input ${errors.endDate ? 'input-error' : ''}`}
                />
                {errors.endDate && (
                  <span className="error-message">{errors.endDate}</span>
                )}
              </div>
            </div>
  
            <div className="form-row">
              <div className="input-group full-width">
                <label>Description<span className="required">*</span></label>
                <textarea
                  rows="4"
                  placeholder="Please provide details about your leave request..."
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setErrors({...errors, description: null});
                  }}
                  className={`form-input ${errors.description ? 'input-error' : ''}`}
                ></textarea>
                {errors.description && (
                  <span className="error-message">{errors.description}</span>
                )}
              </div>
            </div>
  
            <div className="button-section">
              <button
                type="submit"
                className="submit-button"
              >
                Submit Request
              </button>
            </div>
          </div>
        </form>
      </div>
  
      <ConfirmationDialog
        open={openConfirmation}
        handleClose={handleCloseConfirmation}
        handleConfirm={handleSubmit}
        message="Are you sure you want to submit this leave request?"
      />
      <ToastContainer />
    </div>
  );
}

export default LeaveRequest;